// Simple theme state management without Vuex
const THEME_KEY = 'bde_admin_theme'
const BRAND_COLOR_KEY = 'bde_admin_brand_color'
const DEFAULT_BRAND_COLOR = '#c04851'

function normalizeHex(value) {
  if (typeof value !== 'string') return DEFAULT_BRAND_COLOR
  const hex = value.trim()
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toLowerCase() : DEFAULT_BRAND_COLOR
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex).slice(1)
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  }
}

function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map((value) => {
    const hex = Math.max(0, Math.min(255, Math.round(value))).toString(16)
    return hex.padStart(2, '0')
  }).join('')
}

function mixColor(color, target, weight) {
  const sourceRgb = hexToRgb(color)
  const targetRgb = hexToRgb(target)
  return rgbToHex({
    r: sourceRgb.r * (1 - weight) + targetRgb.r * weight,
    g: sourceRgb.g * (1 - weight) + targetRgb.g * weight,
    b: sourceRgb.b * (1 - weight) + targetRgb.b * weight
  })
}

const ThemeManager = {
  listeners: [],
  defaultBrandColor: DEFAULT_BRAND_COLOR,

  isDark() {
    return document.documentElement.classList.contains('dark-mode')
  },

  getBrandColor() {
    return normalizeHex(localStorage.getItem(BRAND_COLOR_KEY) || DEFAULT_BRAND_COLOR)
  },

  applyBrandColor(color) {
    const brandColor = normalizeHex(color)
    const { r, g, b } = hexToRgb(brandColor)
    const root = document.documentElement
    root.style.setProperty('--brand-primary', brandColor)
    root.style.setProperty('--brand-primary-rgb', `${r}, ${g}, ${b}`)
    root.style.setProperty('--brand-primary-light', mixColor(brandColor, '#ffffff', 0.18))
    root.style.setProperty('--brand-primary-lighter', mixColor(brandColor, '#ffffff', 0.88))
    root.style.setProperty('--brand-primary-dark', mixColor(brandColor, '#000000', 0.16))
    root.style.setProperty('--link-color', brandColor)
    root.style.setProperty('--link-hover-color', mixColor(brandColor, '#ffffff', 0.18))
    root.style.setProperty('--bg-menu', this.isDark() ? '#21262d' : brandColor)
  },

  setBrandColor(color) {
    const brandColor = normalizeHex(color)
    localStorage.setItem(BRAND_COLOR_KEY, brandColor)
    this.applyBrandColor(brandColor)
    this.notify()
    return brandColor
  },

  resetBrandColor() {
    localStorage.removeItem(BRAND_COLOR_KEY)
    this.applyBrandColor(DEFAULT_BRAND_COLOR)
    this.notify()
    return DEFAULT_BRAND_COLOR
  },

  toggle() {
    const isDark = !this.isDark()
    if (isDark) {
      document.documentElement.classList.add('dark-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
    }
    localStorage.setItem(THEME_KEY, isDark ? 'dark' : 'light')
    this.applyBrandColor(this.getBrandColor())
    this.notify()
    return isDark
  },

  subscribe(callback) {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback)
    }
  },

  notify() {
    this.listeners.forEach(cb => cb(this.isDark(), this.getBrandColor()))
  },

  init() {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved) {
      if (saved === 'dark') {
        document.documentElement.classList.add('dark-mode')
      }
    } else {
      // Check system preference if no saved preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      if (prefersDark) {
        document.documentElement.classList.add('dark-mode')
      }
    }

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem(THEME_KEY)) {
        if (e.matches) {
          document.documentElement.classList.add('dark-mode')
        } else {
          document.documentElement.classList.remove('dark-mode')
        }
        this.applyBrandColor(this.getBrandColor())
        this.notify()
      }
    })

    this.applyBrandColor(this.getBrandColor())
  }
}

export default ThemeManager
